import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ArrowRight, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { formatAUD } from "@/lib/media";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function GiftCards() {
  useSeo("Gift Cards", "Give the gift of Chewie & Solo happiness — a Happy Bulldog Co. gift card.");
  const { user } = useAuth();
  const [fixed, setFixed] = useState([25, 50, 100]);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState(user?.email || "");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/gift-cards/options").then(({ data }) => setFixed(data.fixed || [25, 50, 100])).catch(() => {});
  }, []);

  const chosen = custom ? parseFloat(custom) || 0 : amount;

  const buy = async (e) => {
    e.preventDefault();
    if (chosen < 10 || chosen > 250) { toast.error("Please choose an amount between $10 and $250."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", {
        amount: chosen, origin_url: window.location.origin,
        purchaser_email: purchaserEmail, recipient_email: recipientEmail,
        recipient_name: recipientName, message,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-[#A78BFA]/15 text-[#19324A] rounded-full px-4 py-1.5 text-sm font-bold"><Gift className="w-4 h-4 text-[#A78BFA]" /> Give a little happiness</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A] mt-4">Gift Cards</h1>
        <p className="text-[#19324A]/60 mt-3 max-w-xl mx-auto">Not sure which book, tee or bandana they'll love? Let them choose. Delivered instantly by email with a code they redeem at checkout.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <form onSubmit={buy} className="space-y-6" data-testid="gift-card-form">
          <div className="bg-white rounded-3xl p-6 soft-shadow">
            <h2 className="font-display font-bold text-xl text-[#19324A] mb-4">Choose an amount</h2>
            <div className="flex gap-3 flex-wrap">
              {fixed.map((v) => (
                <button type="button" key={v} data-testid={`gift-amount-${v}`}
                  onClick={() => { setAmount(v); setCustom(""); }}
                  className={`px-6 py-3 rounded-2xl font-display font-bold border-2 transition-colors ${!custom && amount === v ? "bg-[#A78BFA] text-white border-[#A78BFA]" : "bg-white text-[#19324A] border-[#19324A]/10 hover:border-[#A78BFA]"}`}>
                  {formatAUD(v)}
                </button>
              ))}
            </div>
            <label className="block text-sm font-semibold text-[#19324A]/70 mt-4 mb-1">Or a custom amount ($10–$250)</label>
            <input type="number" min="10" max="250" step="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. 75"
              className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#A78BFA]" data-testid="gift-custom-amount" />
          </div>

          <div className="bg-white rounded-3xl p-6 soft-shadow space-y-3">
            <h2 className="font-display font-bold text-xl text-[#19324A]">Who's it for?</h2>
            <div>
              <label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Recipient's email</label>
              <input type="email" required value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="friend@example.com"
                className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#A78BFA]" data-testid="gift-recipient-email" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Recipient's name (optional)</label>
              <input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Alex"
                className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#A78BFA]" data-testid="gift-recipient-name" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Message (optional)</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={300} placeholder="Happy birthday! Go treat yourself 🐾"
                className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#A78BFA]" data-testid="gift-message" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Your email (we'll send you a copy)</label>
              <input type="email" required value={purchaserEmail} onChange={(e) => setPurchaserEmail(e.target.value)} placeholder="you@example.com"
                className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#A78BFA]" data-testid="gift-purchaser-email" />
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-pink w-full !py-4 text-lg" data-testid="gift-buy-btn">
            <Gift className="w-5 h-5" /> {loading ? "Redirecting…" : `Buy gift card — ${formatAUD(chosen || 0)}`} <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-center text-[#19324A]/45">Secure payment via Stripe. Valid for 3 years. Test card: 4242 4242 4242 4242.</p>
          <p className="text-center text-sm text-[#19324A]/60"><Link to="/gift-cards/balance" className="text-[#A78BFA] font-bold underline" data-testid="check-balance-link">Already have a card? Check its balance</Link></p>
        </form>

        <div className="lg:sticky lg:top-24 self-start">
          <div className="bg-gradient-to-br from-[#A78BFA] to-[#4CC9F0] rounded-3xl p-8 text-white soft-shadow">
            <div className="flex items-center justify-between">
              <span className="font-display font-extrabold text-2xl">Happy Bulldog Co.™</span>
              <Heart className="w-6 h-6" />
            </div>
            <p className="mt-6 text-white/80 text-sm">Gift card value</p>
            <p className="font-display font-extrabold text-5xl mt-1" data-testid="gift-preview-amount">{formatAUD(chosen || 0)}</p>
            <p className="mt-6 text-white/90 text-sm">Redeemable on books, tees, bandanas & bundles — the whole pack of goodies.</p>
          </div>
          <div className="bg-white rounded-3xl p-6 soft-shadow mt-6 text-sm text-[#19324A]/70 space-y-2">
            <p className="font-display font-bold text-[#19324A]">How it works</p>
            <p>1. Pick an amount and add a message.</p>
            <p>2. We email the code to your recipient (and a copy to you).</p>
            <p>3. They enter it at checkout — partial use is fine, the balance stays for next time.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
